import React from "react";

import ApplicationLayout, { applicationStyles as styles } from "../ApplicationLayout";
import WithUserForm from "../../Register/WithUserForm";
import { Cta } from "../fields";

const GENDERS = [
  { value: "MALE", label: "Male" },
  { value: "FEMALE", label: "Female" },
];

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const MIN_AGE = 18;

const pad2 = (n) => String(n).padStart(2, "0");
const digits = (value, max) => String(value || "").replace(/\D/g, "").slice(0, max);

// Parse a saved DOB into { day, month (1-based), year } as strings, or null.
const parseDob = (raw) => {
  if (!raw) return null;
  const text = String(raw);
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return { year: iso[1], month: String(+iso[2]), day: iso[3] };
  const date = /^\d+$/.test(text) ? new Date(Number(text)) : new Date(text);
  if (isNaN(date.getTime())) return null;
  return {
    year: String(date.getUTCFullYear()),
    month: String(date.getUTCMonth() + 1),
    day: pad2(date.getUTCDate()),
  };
};

const IMPOSSIBLE_DAY = "Please enter a valid day.";
const FUTURE_DATE = "Please enter a date in the past.";
const CHECK_YEAR = "Please enter a valid year.";
const TOO_YOUNG = `You must be ${MIN_AGE} or over to apply.`;

const yearError = ({ year }) => {
  if (year.length < 4) return "";
  const y = Number(year);
  const thisYear = new Date().getFullYear();
  if (y > thisYear) return FUTURE_DATE;
  if (y < 1900) return CHECK_YEAR;
  return thisYear - y < MIN_AGE ? TOO_YOUNG : "";
};

const dayError = ({ day, month, year }) => {
  if (day.length < 2) return "";
  const d = Number(day);
  const longestMonth = month && year.length === 4 ? new Date(Number(year), Number(month), 0).getDate() : 31;
  return d < 1 || d > longestMonth ? IMPOSSIBLE_DAY : "";
};

const dateOfBirthError = ({ day, month, year }) => {
  if (!day || !month || !year) return "Please enter your full date of birth.";
  if (year.length !== 4) return "Please enter the year as 4 digits.";

  const d = Number(day);
  const m = Number(month);
  const y = Number(year);

  if (d < 1 || d > new Date(y, m, 0).getDate()) return IMPOSSIBLE_DAY;
  if (new Date(y, m - 1, d) > new Date()) return FUTURE_DATE;
  if (y < 1900) return CHECK_YEAR;

  const now = new Date();
  const hadBirthday = now.getMonth() + 1 > m || (now.getMonth() + 1 === m && now.getDate() >= d);
  if (now.getFullYear() - y - (hadBirthday ? 0 : 1) < MIN_AGE) return TOO_YOUNG;

  return "";
};

export default class StepAboutYou extends React.Component {
  state = { gender: "", day: "", month: "", year: "", tried: false };

  initialized = false;

  initFromUser = (currentUser) => {
    if (this.initialized || !currentUser) return;
    this.initialized = true;
    const parsed = parseDob(currentUser.dateOfBirth);
    this.setState({ gender: currentUser.gender || "", ...(parsed || {}) });
  };

  async onSubmit(values, _, props) {
    const { gender, day, month, year } = this.state;
    if (!gender || dateOfBirthError(this.state)) {
      this.setState({ tried: true });
      return;
    }
    const dateOfBirth = `${year}-${pad2(month)}-${pad2(day)}`;
    const input = WithUserForm.formatUserAboutInput({ id: values.id, gender, dateOfBirth });
    await props.updateUserAbout({ variables: { input } });
    return props.machine.service.send({ type: "NEXT" });
  }

  render() {
    const { gender, day, month, year, tried } = this.state;
    const dobError = dateOfBirthError(this.state);
    const liveDayError = dayError(this.state);
    const liveYearError = yearError(this.state);
    const showGenderError = tried && !gender;
    const showDobError = tried && !!dobError;
    const dobMessage = liveDayError || liveYearError || (showDobError ? dobError : "");

    return (
      <WithUserForm onSubmit={(values, _, props) => this.onSubmit(values, _, props)}>
        {({ form: { handleSubmit, submitting }, userQuery: { data } }) => {
          this.initFromUser(data?.currentUser);

          const footer = (
            <>
              <div className={styles.actions}>
                <Cta submitting={submitting}>Continue</Cta>
                <p className={styles.footerNote}>
                  Having trouble? <a href="https://wa.me/443330166335">Contact us</a>
                </p>
              </div>
            </>
          );

          return (
            <ApplicationLayout
              as="form"
              onSubmit={handleSubmit}
              step={3}
              title="About you"
              intro="We need this so you can create or accept jobs, open a store and receive payments."
              footer={footer}
            >
              <fieldset className={styles.fieldset}>
                <legend className={styles.legend}>Gender</legend>
                <div
                  role="radiogroup"
                  aria-label="Gender"
                  className={`${styles.segmented} ${showGenderError ? styles.segmentedInvalid : ""}`}
                >
                  {GENDERS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={gender === option.value}
                      className={`${styles.segment} ${gender === option.value ? styles.segmentSelected : ""}`}
                      onClick={() => this.setState({ gender: option.value })}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                {showGenderError && <div className={styles.error}>Please choose one.</div>}
              </fieldset>

              <fieldset className={styles.fieldset}>
                <legend className={styles.legend}>Date of birth</legend>
                <div className={styles.dobRow}>
                  <div className={styles.dobPart}>
                    <label className={styles.subLabel} htmlFor="dobDay">Day</label>
                    <div className={`${styles.box} ${styles.inputBox} ${showDobError || liveDayError ? styles.boxInvalid : ""}`}>
                      <input
                        id="dobDay"
                        className={styles.input}
                        type="text"
                        inputMode="numeric"
                        autoComplete="bday-day"
                        maxLength={2}
                        placeholder="DD"
                        value={day}
                        onChange={(e) => this.setState({ day: digits(e.target.value, 2) })}
                      />
                    </div>
                  </div>

                  <div className={styles.dobPart}>
                    <label className={styles.subLabel} htmlFor="dobMonth">Month</label>
                    <div className={`${styles.box} ${styles.inputBox} ${styles.selectWrap} ${showDobError ? styles.boxInvalid : ""}`}>
                      <select
                        id="dobMonth"
                        className={`${styles.input} ${styles.select}`}
                        autoComplete="bday-month"
                        value={month}
                        onChange={(e) => this.setState({ month: e.target.value })}
                      >
                        <option value="">Month</option>
                        {MONTHS.map((label, i) => (
                          <option key={label} value={String(i + 1)}>{label}</option>
                        ))}
                      </select>
                      <svg className={styles.selectArrow} width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#B08D57" strokeWidth="1.5" aria-hidden="true">
                        <path d="M2.5 4.5 6 8l3.5-3.5" />
                      </svg>
                    </div>
                  </div>

                  <div className={styles.dobPart}>
                    <label className={styles.subLabel} htmlFor="dobYear">Year</label>
                    <div className={`${styles.box} ${styles.inputBox} ${showDobError || liveYearError ? styles.boxInvalid : ""}`}>
                      <input
                        id="dobYear"
                        className={styles.input}
                        type="text"
                        inputMode="numeric"
                        autoComplete="bday-year"
                        maxLength={4}
                        placeholder="YYYY"
                        value={year}
                        onChange={(e) => this.setState({ year: digits(e.target.value, 4) })}
                      />
                    </div>
                  </div>
                </div>
                <div className={styles.fieldHint}>You must be {MIN_AGE} or over to apply.</div>
                {dobMessage && <div className={styles.error}>{dobMessage}</div>}
              </fieldset>
            </ApplicationLayout>
          );
        }}
      </WithUserForm>
    );
  }
}
